import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { PiCoinBold } from "react-icons/pi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Common_Event_Hit } from "../../Actions/UserEvents";
import {
  redeemPoints,
  sendMobileOtp,
  validateOtp,
  addCoinsOnRedemption,
} from "../../Actions/CureCoin";

const RedeemCoinModal = ({
  show,
  onHide,
  coinBalance,
  verifiedMobileNumber,
  patientId,
  setCoinBalance,
}) => {
  const [redeemAmount, setRedeemAmount] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [redemptionResult, setRedemptionResult] = useState(null);

  const getRetailerCode = () => {
    let onBehalfRetCode = localStorage.getItem("onBehalfRetCode");
    let userData = localStorage.getItem("userData");
    if (userData) {
      userData = JSON.parse(userData);
      if (
        onBehalfRetCode &&
        onBehalfRetCode.length > 0 &&
        onBehalfRetCode !== "undefined" &&
        (userData.admin || userData.salesPerson)
      ) {
        return onBehalfRetCode;
      } else {
        return userData.retailerCode || "";
      }
    }
    return "";
  };

  // Pre-fill mobile number when modal opens
  useEffect(() => {
    if (show && verifiedMobileNumber) {
      setMobileNumber(verifiedMobileNumber);
    }
  }, [show, verifiedMobileNumber]);

  // Timer countdown for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Format timer display (MM:SS)
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Validate redeem amount
  const validateRedeemAmount = (value) => {
    const amount = parseInt(value);
    if (isNaN(amount) || amount <= 0) {
      setErrorMessage("Please enter a valid coin amount");
      return false;
    }
    if (amount > coinBalance) {
      setErrorMessage(
        `You don't have enough coins. Your balance is ${coinBalance} coins.`,
      );
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const validateMobileNumber = (number) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(number)) {
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const handleRedeemAmountChange = (e) => {
    const value = e.target.value;
    setRedeemAmount(value);
    if (value) {
      validateRedeemAmount(value);
    } else {
      setErrorMessage("");
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
    if (value.length === 10) {
      validateMobileNumber(value);
    } else {
      setErrorMessage("");
    }
  };

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!validateRedeemAmount(redeemAmount)) {
      return;
    }
    if (!validateMobileNumber(mobileNumber)) {
      return;
    }

    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_send_otp",
      redeemAmount,
      mobileNumber,
    });

    setOtpLoading(true);
    try {
      await sendMobileOtp({ mobileNumber });
      setOtpSent(true);
      setTimer(120); // 2 minutes = 120 seconds
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error?.error ||
          "Failed to send OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (otpLoading) {
      return;
    }
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_resend_otp",
    });

    setOtpLoading(true);
    try {
      await sendMobileOtp({ mobileNumber });
      setTimer(120);
      setOtp("");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error?.error ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle Submit
  const handleSubmit = async () => {
    if (!otp || otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_redeem_submit",
      redeemAmount,
      otp,
    });

    try {
      const otpResponse = await validateOtp({
        mobileNumber,
        otp,
      });

      if (otpResponse?.data?.status !== "OK") {
        setErrorMessage("Invalid OTP. Please try again.");
        setLoading(false);
        return;
      }

      const response = await redeemPoints({
        patientId: patientId,
        retailerCode: getRetailerCode(),
        redeemPoints: parseInt(redeemAmount),
        serviceType: "MED",
      });

      if (response?.data?.success) {
        setRedemptionResult(response.data);
        setCoinBalance(response.data.currentBalance);

        const invoiceNumber = mobileNumber; // Useing mobile number as invoice number
        const retailerCode = response?.data?.retailerCode || getRetailerCode();

        try {
          await addCoinsOnRedemption({
            retailerCode,
            invoiceNumber,
            amount: parseInt(redeemAmount),
          });
        } catch (addCoinError) {
          console.error("Failed to add coins on redemption:", addCoinError);
        }

        setShowSuccess(true);
      } else {
        setErrorMessage(
          response?.data?.message || "Redemption failed. Please try again.",
        );
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Redemption failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Success Close
  const handleSuccessClose = () => {
    setShowSuccess(false);
    handleClose();
  };

  // Handle Close
  const handleClose = () => {
    setRedeemAmount("");
    setMobileNumber(verifiedMobileNumber || "");
    setOtp("");
    setErrorMessage("");
    setOtpSent(false);
    setTimer(0);
    setRedemptionResult(null);
    onHide();
  };

  return (
    <>
      {/* Redeem Modal */}
      <Modal
        show={show && !showSuccess}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title className="font_ubuntu color_primary">
            <PiCoinBold style={{ color: "#FFD700", marginRight: "8px" }} />
            Redeem Cure Coins
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && (
            <Alert variant="danger" className="font_14">
              {errorMessage}
            </Alert>
          )}

          {/* Redeem Amount Field */}
          <Form.Group className="mb-3">
            <Form.Label className="font_ubuntu font_14 font_weight_md">
              Enter Coins to Redeem
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter coin amount"
              value={redeemAmount}
              onChange={handleRedeemAmountChange}
              disabled={otpSent}
              className="font_ubuntu"
            />
            <Form.Text className="text-muted font_12">
              Available Balance: {coinBalance} coins
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="font_ubuntu font_14 font_weight_md">
              Mobile Number
            </Form.Label>
            <Form.Control
              type="tel"
              placeholder="Enter 10-digit mobile number"
              value={mobileNumber}
              onChange={handleMobileNumberChange}
              disabled={true}
              maxLength={10}
              className="font_ubuntu"
              style={{ backgroundColor: "#f8f9fa" }}
            />
            <Form.Text className="text-muted font_12">
              Verified mobile number from Cure Coin
            </Form.Text>
          </Form.Group>

          {/* OTP Field (shown after OTP sent) */}
          {otpSent && (
            <Form.Group className="mb-3">
              <Form.Label className="font_ubuntu font_14 font_weight_md">
                Enter OTP
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(value);
                  setErrorMessage("");
                }}
                maxLength={6}
                className="font_ubuntu"
              />
              <Form.Text className="text-muted font_12">
                {timer > 0 ? (
                  <>OTP expires in {formatTimer(timer)}</>
                ) : (
                  <>
                    Didn't receive OTP?{" "}
                    {otpLoading ? (
                      <span className="color_secondary">Sending...</span>
                    ) : (
                      <span
                        onClick={handleResendOtp}
                        style={{
                          color: "#18406E",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}>
                        Resend OTP
                      </span>
                    )}
                  </>
                )}
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            className="font_ubuntu">
            Cancel
          </Button>
          {!otpSent ? (
            <Button
              className="bg_primary brdr_none color_white font_ubuntu"
              onClick={handleSendOtp}
              disabled={!redeemAmount || !mobileNumber || otpLoading}>
              {otpLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                "Send OTP"
              )}
            </Button>
          ) : (
            <Button
              className="bg_secondary brdr_none color_white font_ubuntu"
              onClick={handleSubmit}
              disabled={!otp || otp.length !== 6 || loading}>
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"></span>
              ) : (
                "Submit"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal
        show={showSuccess}
        onHide={handleSuccessClose}
        centered
        backdrop="static">
        <Modal.Body className="text-center py-5">
          <AiOutlineCheckCircle
            style={{ fontSize: "80px", color: "#28a745", marginBottom: "20px" }}
          />
          <h4 className="color_primary font_ubuntu mb-3">
            Redemption Successful!
          </h4>
          <p className="color_grey2 mb-2">
            You have successfully redeemed{" "}
            <b>{redemptionResult?.redeemedPoints || redeemAmount}</b> Cure Coins
          </p>
          <p className="color_grey2 mb-2">
            Discount of{" "}
            <b className="color_secondary">
              ₹{redemptionResult?.redemptionValue || redeemAmount}
            </b>{" "}
            will be applied to your next order
          </p>
          <p className="color_grey2 font_12">
            New Balance: <b>{redemptionResult?.currentBalance}</b> coins
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="w-100 bg_primary brdr_none color_white font_ubuntu"
            onClick={handleSuccessClose}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RedeemCoinModal;
